import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { formatPublishedDate, useTitle } from "../../../../config/global";
import "../../css/setup.css";

import Layout from '../../../Layout/Layout'

import { Button } from '../../../../components/ui/Button';

import { useGetCampaign, useGetCampaignLayerContent, usePublishCampaignMutation, useResetCampaignMutation, useUpdateCampaignMutation } from '../../../../queries';
import { setHideTabs, setCampaignDetails, setLayer, toggleDialog, setMediaIdReset } from '../../../../redux/Slices/Global/globalSlice';
import CampaignContentSetup from './component/CampaignContentSetup';
import PreviewCampaign from './component/PreviewCampaign';
import AssignPopup from '../AssignToDevicePopup';
import { Controller, useForm } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { useQueryClient } from 'react-query';
import { setCampaignPreview } from '../../../../redux/Slices/campaignSlice';
import CampaignSettings from './component/settings/CampaignSettings';
import { useCustomToast } from '../../../Contexts/ToastContext';

/**
 * Layer-3 campaign builder — SHELL, ported pixel-for-pixel from the approved
 * prototype (.claude/pp-campaign-builder-prototype.html): sticky appbar
 * (back + title + status chip + toolbar that collapses to Publish + a ⋮
 * overflow under ~720px) and an "unpublished changes"/publish-blocked
 * notice line. Owns the same pinned-toolbar host pattern
 * CampaignContentSetup used to own (see setup.css) — that component is now
 * just the scrollable body (المحتوى الرئيسي + الصور section).
 *
 * CampaignSettings / AssignToDevicePopup / PreviewCampaign are UNTOUCHED —
 * this file only opens them exactly as it did before the redesign.
 */
function CampaignSetup() {
  useTitle("Campaign Setup")
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { control } = useForm();
  const { showToast } = useCustomToast();

  const { campaignPreview } = useSelector((state) => state.campaign);
  const { isPrayerCampaign, campaignDetails, openDialog, isLoading } = useSelector((state) => state.global);
  const { isLoading: campaignLoading, data: campaignInfo } = useGetCampaign({ enabled: true, id });
  // Layer-3: the builder only ever shows/edits MAIN now — no more layer
  // dropdown, so this is a literal, not the (removed) redux `layer` switch.
  const { isLoading: layerLoading, data, refetch, isError } = useGetCampaignLayerContent(id, "MAIN");
  const publishCampaign = usePublishCampaignMutation()
  const resetCampaign = useResetCampaignMutation()

  const [ovfOpen, setOvfOpen] = useState(false);
  const ovfRef = useRef(null);
  const inputRef = useRef(null);
  const [titleValue, setTitleValue] = useState();
  const [editTitle, setEditTitle] = useState(false);

  const updateCampaign = useUpdateCampaignMutation();
  const queryClient = useQueryClient();

  const isDefaultPrayer = !JSON.parse(sessionStorage.getItem('is_default_prayer'));
  const isMainEmpty = campaignDetails.campaign_duration === 0;
  const loading = layerLoading;
  const campaignPublishResetLoading = publishCampaign.isLoading || resetCampaign.isLoading || isLoading;

  useEffect(() => {
    if (!isEmpty(campaignInfo)) {
      dispatch(setCampaignDetails(campaignInfo));
      setTitleValue(campaignInfo.name)
      dispatch(setHideTabs(false))
    }
  }, [campaignInfo])

  useEffect(() => {
    dispatch(setLayer("MAIN"))
    return () => {
      dispatch(setCampaignDetails({}))
      dispatch(setMediaIdReset([]))
      dispatch(setCampaignPreview(false))
    }
  }, []);

  // Close the mobile overflow menu on outside click (prototype behavior).
  useEffect(() => {
    const onDocClick = (e) => {
      if (ovfRef.current && !ovfRef.current.contains(e.target)) setOvfOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const publish = async () => {
    await publishCampaign.mutateAsync({ id, name: campaignInfo?.name }, {
      onSuccess: () => showToast('success', t('buttons.publish'), t('show.show_publish_success')),
    })
  }

  const reset = async () => {
    await resetCampaign.mutateAsync({ id, name: campaignInfo?.name }, {
      onSuccess: () => showToast('success', t('show.show_discard_changes'), t('show.show_discard_success')),
    })
  }

  const handleSubmitTag = async (name) => {
    await updateCampaign.mutateAsync({
      id: campaignInfo.id,
      data: { "status": campaignInfo.status, "name": name },
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries(['getCampaign', campaignInfo.id])
        showToast('success', t('show.show_update_campaign'), t('show.show_save_success'));
      }
    })
  }

  const disablePublishBtn = useMemo(() => {
    return loading || campaignDetails.has_unpublished_changes !== true || isMainEmpty;
  }, [loading, campaignDetails.has_unpublished_changes, isMainEmpty]);

  const canDiscard = campaignDetails.has_unpublished_changes === true && campaignDetails.neverPublished !== true;
  const isLive = campaignDetails.neverPublished !== true;

  const assignDisabled = loading || (campaignDetails.is_playable === undefined ? isMainEmpty : !campaignDetails.is_playable);
  const previewDisabled = isMainEmpty;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setEditTitle(false);
        setTitleValue(campaignDetails.name);
      }
    };
    if (editTitle) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editTitle, campaignDetails.name]);

  const TitleEditor = useMemo(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmitTag(titleValue);
        setEditTitle(false);
      }
    };

    if (campaignLoading) return <h1>…</h1>;

    if (editTitle && titleValue != null) {
      return (
        <div ref={inputRef} className="w-100">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <InputText
                {...field}
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                maxLength={30}
                placeholder={t('show.show_enter_title')}
                className="w-100 titleCampaignEditor focus:border-blue-500 outline-none bg-transparent shadow-none text-sm p-0 transition-all"
              />
            )}
          />
        </div>
      );
    }

    return (
      <h1 className="cursor-pointer group d-flex align-items-center gap-2" onClick={() => setEditTitle(true)}>
        {t('show.show_campaign_title', { name: campaignDetails.name })}
        {updateCampaign.isLoading ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
        ) : (
          <i className="pl-3 fa-solid fa-pen fa-xs opacity-0 group-hover:opacity-100 transition-opacity duration-150"></i>
        )}
      </h1>
    );
  }, [editTitle, titleValue, campaignDetails.name, campaignLoading, control]);

  const dateLine = campaignLoading
    ? ""
    : campaignDetails?.has_unpublished_changes === true
      ? t('show.show_updated_at', { date: formatPublishedDate(campaignDetails?.updated_at) })
      : t('show.show_published_at', { date: formatPublishedDate(campaignDetails?.updated_at) });

  // Publish-blocked reason wins over the generic "unpublished changes"
  // notice — the user must always be told the ONE reason that matters most.
  const noticeText = isMainEmpty
    ? t('show.show_publish_blocked_empty_main')
    : campaignDetails.has_unpublished_changes === true
      ? t('show.show_unpublished_notice')
      : null;

  const overflowActions = [
    { key: 'assign', label: t('show.show_assign_to_screens'), icon: 'bx bx-up-arrow-alt', disabled: assignDisabled, onClick: () => dispatch(toggleDialog("assignDeviceGroup")), hide: isPrayerCampaign },
    { key: 'settings', label: t('show.show_settings'), icon: 'pi pi-cog', disabled: loading, onClick: () => dispatch(toggleDialog("campaignSettings")) },
    { key: 'preview', label: campaignPreview ? t('buttons.edit') : t('buttons.preview'), icon: 'pi pi-eye', disabled: previewDisabled, onClick: () => dispatch(setCampaignPreview(!campaignPreview)), hide: isPrayerCampaign },
  ].filter((a) => !a.hide);

  // Page shell's pinned appbar — rendered from the page BODY, not through
  // Layout's subNavbarProps/Navbar `content` slot. That slot's own wrapper
  // is `d-xl-none` (mobile/tablet only, confirmed in Navbar.jsx) — it would
  // have made this whole header (and the old toolbar before it) invisible
  // above the ~1200px breakpoint. Reusing the SAME pinned-toolbar host
  // pattern this page's content pane already relied on (`.pp-campaign-
  // setup-host`, see setup.css) keeps the appbar visible and sticky at
  // every width, matching the prototype's own breakpoint (719px) instead
  // of Bootstrap's.
  const appbar = useMemo(() => {
    if (!isDefaultPrayer) return null;

    return (
      <div className="pp-cb-appbar">
        <div className="pp-cb-wrap">
        <div className="pp-cb-bar-row">
          <button className="pp-cb-iconbtn pp-cb-back" aria-label={t('buttons.back')} onClick={() => navigate('/campaigns/list')}>
            <i className="pi pi-arrow-right" style={{ fontSize: 19 }}></i>
          </button>

          <div className="pp-cb-htitle">
            {TitleEditor}
            <div className="pp-cb-sub">
              <span className={`pp-cb-status-chip ${isLive ? 'live' : 'draft'}`}>
                <span className="d"></span>
                {isLive ? t('show.show_status_live') : t('show.show_status_draft')}
              </span>
              {dateLine && (
                <>
                  <span className="pp-cb-dotsep"></span>
                  <span>{dateLine}</span>
                </>
              )}
            </div>
          </div>

          <div className="pp-cb-toolbar">
            <div className="pp-cb-desk">
              {!isPrayerCampaign && (
                <Button
                  className="p-button-text"
                  label={campaignPreview ? t('buttons.edit') : t('buttons.preview')}
                  icon={<i className="pi pi-eye" />}
                  disabled={previewDisabled}
                  onClick={() => dispatch(setCampaignPreview(!campaignPreview))}
                />
              )}
              <Button
                className="p-button-outlined"
                label={t('show.show_settings')}
                icon={<i className="pi pi-cog" />}
                disabled={loading}
                onClick={() => dispatch(toggleDialog("campaignSettings"))}
              />
              {!isPrayerCampaign && (
                <Button
                  className="p-button-outlined"
                  label={t('show.show_assign_to_screens')}
                  icon={<i className="bx bx-up-arrow-alt" />}
                  disabled={assignDisabled}
                  onClick={() => dispatch(toggleDialog("assignDeviceGroup"))}
                />
              )}
            </div>

            {/* Always visible, both widths — .pp-cb-desk (Preview/Settings/
                Assign) is what collapses under 719px, matching the prototype
                where Publish sits outside .desk as its own permanent action. */}
            <Button
              id="publish-btn"
              data-pr-tooltip={disablePublishBtn && isMainEmpty ? t('show.show_publish_blocked_empty_main') : undefined}
              data-pr-position="bottom"
              label={t('buttons.publish')}
              icon={<i className={campaignPublishResetLoading ? "pi pi-spin pi-spinner" : "pi pi-check"} />}
              disabled={disablePublishBtn}
              onClick={publish}
            />

            <div className="pp-cb-ovf" ref={ovfRef}>
              <button className="pp-cb-iconbtn" aria-label={t('show.show_more_options')} onClick={() => setOvfOpen((v) => !v)}>
                <i className="pi pi-ellipsis-v" style={{ fontSize: 19 }}></i>
              </button>
              {ovfOpen && (
                <div className="pp-cb-ovf-menu">
                  {overflowActions.map((a) => (
                    <button key={a.key} disabled={a.disabled} onClick={() => { setOvfOpen(false); a.onClick(); }}>
                      <i className={a.icon}></i>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {noticeText && (
          <div className="pp-cb-notice">
            <i className="pi pi-info-circle"></i>
            <span className="pp-cb-notice-grow">{noticeText}</span>
            {canDiscard && (
              <button className="pp-cb-notice-discard" onClick={reset}>{t('show.show_discard_changes')}</button>
            )}
          </div>
        )}

        {campaignPublishResetLoading && (
          <div className="pp-cb-notice">
            <i className="pi pi-spin pi-spinner"></i>
            <span className="pp-cb-notice-grow">{t('show.show_saving')}</span>
          </div>
        )}

        </div>
      </div>
    );
  }, [campaignDetails, loading, campaignPublishResetLoading, isMainEmpty, campaignPreview, isLoading, ovfOpen, TitleEditor, isLive, dateLine, noticeText, canDiscard]);

  // Keep the shared body-scroll-containment class (see setup.css) — this
  // page owns the pinned appbar now, but the same host/scroll split still
  // applies while any campaign-setup pane is mounted.
  useEffect(() => {
    document.body.classList.add('pp-campaign-setup-active');
    return () => document.body.classList.remove('pp-campaign-setup-active');
  }, []);

  return (
    <>
      <Layout padding={false} subNavbarProps={{}} subNavbarContent={null}>
        <div className="pp-campaign-setup-host">
          {appbar}
          <div className="pp-campaign-setup-scroll">
            {campaignPreview ? (
              // Full-bleed, untouched — PreviewCampaign.jsx owns its own
              // width/layout and must NOT be capped by .pp-cb-wrap.
              <PreviewCampaign />
            ) : (
              <div className="pp-cb-wrap">
                <CampaignContentSetup loading={loading} data={data} refetch={refetch} isError={isError} />
              </div>
            )}
          </div>
        </div>
      </Layout>
      {openDialog.campaignSettings ? <CampaignSettings showOption={false} campaignId={id} /> : <></>}
      {openDialog.assignDeviceGroup ? <AssignPopup campaignId={id} /> : <></>}
    </>
  )
}

export default CampaignSetup
